import { roundToTwoDecimals } from '@fruitful/fruitils';
import cloneDeep from 'lodash/cloneDeep';

import { InvestmentGrowthSimulator } from '../investments/InvestmentGrowthSimulator';
import { MarginalTaxRateCalculator } from '../taxRate/MarginalTaxRateCalculator';
import { RetirementContributionCalculator } from './RetirementContributionCalculator';
import { RetirementContributionWaterfallGenerator } from './RetirementContributionWaterfallGenerator';
import { RetirementDistributionCalculator } from './RetirementDistributionCalculator';
import { SocialSecurityBenefitCalculator } from './SocialSecurityBenefitCalculator';
import {
    type AnnualCashFlow,
    type MemberInput,
    type RetirementAssets,
    type RetirementCalculatorInputs,
    type RetirementCalculatorOutputs,
    type RetirementContributions,
    type RetirementContributionWaterfall,
    type RetirementDistribution,
    type RetirementGrowth,
    type SimulatedRetirementSavingsOutcome,
    YearType,
} from './types';

enum SimulationMode {
    EXPECTATION = 'EXPECTATION',
    RANDOM_DRAW = 'RANDOM_DRAW',
}

// Given a target success likelihood (probability), calculates the required annual retirement savings needed to achieve that target, along with simulated cash flows for all retirement accounts over many years, for an incremented set of annual retirement savings.
export class RetirementCalculator {
    private readonly ANNUAL_RETIREMENT_SAVINGS_INCREMENT = 500;
    private readonly MONTE_CARLO_SIMULATIONS = 500; // Adjust for performance.

    constructor(private readonly inputs: RetirementCalculatorInputs) {}

    public calculate(): RetirementCalculatorOutputs {
        this.populateDefaults();

        const simulatedRetirementSavingsOutcomes =
            this.simulateRetirementSavings();

        const requiredAnnualRetirementSavings =
            Object.keys(simulatedRetirementSavingsOutcomes)
                .map(Number)
                .sort((a, b) => a - b)
                .find((annualSavings) => {
                    const outcome = simulatedRetirementSavingsOutcomes[
                        annualSavings
                    ] as SimulatedRetirementSavingsOutcome;
                    return (
                        outcome.successLikelihood >=
                        this.inputs.householdInputs.targetSuccessLikelihood
                    );
                }) ?? 0;

        // Output the initial contributions required to each account. Note: This may (likely will) change over the course of the life of the member, but we output this value to be useful to answer the question of what needs to be contributed today.
        const requiredAnnualContributions = (
            (
                simulatedRetirementSavingsOutcomes[
                    requiredAnnualRetirementSavings
                ] as SimulatedRetirementSavingsOutcome
            ).averageCashFlowByYear[0] as AnnualCashFlow
        ).contributions;

        return {
            requiredAnnualRetirementSavings,
            requiredAnnualContributions,
            annualSavingsOutcomes: simulatedRetirementSavingsOutcomes,
        };
    }

    private populateDefaults() {
        if (this.inputs.householdInputs.currentMarginalTaxRate == null) {
            const grossAnnualIncome = this.inputs.memberInputs.reduce(
                (acc, member) => acc + member.currentGrossAnnualIncome,
                0,
            );
            const taxRateCalculator = new MarginalTaxRateCalculator(
                grossAnnualIncome,
                this.inputs.householdInputs.residentialLocation,
                this.inputs.householdInputs.taxFilingStatus,
            );

            this.inputs.householdInputs.currentMarginalTaxRate =
                taxRateCalculator.calculate();
        }

        if (
            this.inputs.householdInputs.retirementContributionWaterfall == null
        ) {
            this.inputs.householdInputs.retirementContributionWaterfall =
                new RetirementContributionWaterfallGenerator(
                    this.inputs.householdInputs.currentMarginalTaxRate,
                    this.inputs.memberInputs,
                ).generate();
        }

        this.inputs.memberInputs.forEach((member) => {
            if (member.annualSocialSecurityBenefit === undefined) {
                const socialSecurityBenefitCalculator =
                    new SocialSecurityBenefitCalculator(
                        member.currentAge,
                        member.socialSecurityBenefitAge,
                        member.currentGrossAnnualIncome,
                    );

                member.annualSocialSecurityBenefit =
                    socialSecurityBenefitCalculator.calculate();
            }
        });
    }

    private simulateRetirementSavings(): Record<
        number,
        SimulatedRetirementSavingsOutcome
    > {
        let annualRetirementSavings = 0;
        let successLikelihood = 0;
        let iterationCount = 0;
        const maxIterations = 200; // Helpful to prevent infinite loop.
        const minIterations = 3; // Helpful to debug output.

        const outcomes: Record<number, SimulatedRetirementSavingsOutcome> = {};

        while (
            (successLikelihood < 1.0 || iterationCount < minIterations) &&
            iterationCount < maxIterations
        ) {
            const outcome = this.simulateRetirementSavingsOutcome(
                annualRetirementSavings,
            );
            successLikelihood = outcome.successLikelihood;
            outcomes[String(annualRetirementSavings)] = outcome;
            annualRetirementSavings += this.ANNUAL_RETIREMENT_SAVINGS_INCREMENT;
            iterationCount++;
        }

        return outcomes;
    }

    private simulateRetirementSavingsOutcome(
        annualRetirementSavings: number,
    ): SimulatedRetirementSavingsOutcome {
        const averageCase = this.simulateCashFlowByYear(
            annualRetirementSavings,
            SimulationMode.EXPECTATION,
        );

        let successes = 0;

        for (let i = 0; i < this.MONTE_CARLO_SIMULATIONS; i++) {
            const cashFlowByYear = this.simulateCashFlowByYear(
                annualRetirementSavings,
                SimulationMode.RANDOM_DRAW,
            );

            if (this.isSuccessful(cashFlowByYear)) {
                successes++;
            }
        }

        return {
            successLikelihood: successes / this.MONTE_CARLO_SIMULATIONS,
            averageCashFlowByYear: averageCase,
        };
    }

    private isSuccessful(cashFlowByYear: AnnualCashFlow[]): boolean {
        if (cashFlowByYear.length === 0)
            throw new Error('No years in cash flow');

        // Check for sufficient funds for each year
        return cashFlowByYear.every((year) => !year.insufficientFunds);
    }

    private simulateCashFlowByYear(
        annualRetirementSavings: number,
        mode: SimulationMode,
    ): AnnualCashFlow[] {
        const cashFlowByYear: AnnualCashFlow[] = [];
        let startingAssets: RetirementAssets = this.inputs.memberInputs.flatMap(
            (member) => member.currentRetirementSavings,
        );

        for (let year = 1; year <= this.getMaxYear(); year++) {
            const cashFlow = this.simulateRetirementYear(
                year,
                annualRetirementSavings,
                startingAssets,
                mode,
            );
            cashFlowByYear.push(cashFlow);
            startingAssets = cashFlow.endingAssets;
        }

        return cashFlowByYear;
    }

    private getMaxYear(): number {
        return Math.max(
            ...this.inputs.memberInputs.map(
                ({ lifeExpectancy, currentAge }) => lifeExpectancy - currentAge,
            ),
        );
    }

    private getYearType(age: number, member: MemberInput): YearType {
        if (age >= member.lifeExpectancy) return YearType.END_OF_LIFE;
        if (age >= member.retirementAge) return YearType.DECUMULATION;
        return YearType.ACCUMULATION;
    }

    private getSocialSecurityBenefit(age: number, member: MemberInput): number {
        if (age >= member.socialSecurityBenefitAge)
            return member.annualSocialSecurityBenefit as number; // This field is populated as a number as part of `populateDefaults()`.
        return 0;
    }

    private simulateRetirementYear(
        year: number,
        annualRetirementSavings: number,
        startingAssets: RetirementAssets,
        mode: SimulationMode,
    ): AnnualCashFlow {
        // primary member info
        const primaryMember = this.inputs.memberInputs[0] as MemberInput;
        const primaryMemberAge = primaryMember.currentAge + year; // Year 1 is the first year, and is the next calendar year.
        const primaryMemberYearType = this.getYearType(
            primaryMemberAge,
            primaryMember,
        );
        const primaryMemberSocialSecurityBenefit =
            this.getSocialSecurityBenefit(primaryMemberAge, primaryMember);

        // partner member info
        let partnerMemberAge: number | undefined;
        let partnerMemberYearType: YearType | undefined;
        let partnerMemberSocialSecurityBenefit: number | undefined;

        if (this.inputs.memberInputs.length > 1) {
            const partnerMember = this.inputs.memberInputs[1] as MemberInput;
            partnerMemberAge = partnerMember.currentAge + year;
            partnerMemberYearType = this.getYearType(
                partnerMemberAge,
                partnerMember,
            );
            partnerMemberSocialSecurityBenefit = this.getSocialSecurityBenefit(
                partnerMemberAge,
                partnerMember,
            );
        }

        // retirement expenses breakdown
        const {
            retirementExpenses,
            retirementHealthcareCosts,
            longTermCareCosts,
            totalRetirementExpenses,
        } = this.getRetirementExpenses(
            [
                primaryMemberYearType,
                ...(partnerMemberYearType != null
                    ? [partnerMemberYearType]
                    : []),
            ],
            year,
        );

        // asset changes - only calculate contributions if at least one member is still accumulating (not retired)
        const isAnyMemberStillAccumulating = [
            primaryMemberYearType,
            ...(partnerMemberYearType != null ? [partnerMemberYearType] : []),
        ].some((yearType) => yearType === YearType.ACCUMULATION);

        const contributions = isAnyMemberStillAccumulating
            ? new RetirementContributionCalculator(
                  annualRetirementSavings,
                  this.inputs.householdInputs
                      .retirementContributionWaterfall as RetirementContributionWaterfall, // This field is populated as a RetirementContributionWaterfall as part of `populateDefaults()`.
                  this.inputs.memberInputs,
                  startingAssets,
                  year, // Pass current year for age-based contribution restrictions
              ).calculate()
            : [];

        // Create a combined assets array that includes both starting assets and newly proposed accounts
        const allAssets = cloneDeep(startingAssets);
        contributions.forEach(({ account }) => {
            const existingAccount = allAssets.find(
                ({ id }) => id === account.id,
            );
            if (!existingAccount) {
                // This is a newly proposed account, add it to allAssets
                allAssets.push(account);
            }
        });

        const growth: RetirementGrowth = allAssets.map((account) => {
            const growthCalculator = new InvestmentGrowthSimulator(
                account.balance,
                account.equitiesPercentage,
            );

            return {
                account,
                growth:
                    mode === SimulationMode.EXPECTATION
                        ? growthCalculator.getExpectedAnnualGrowth()
                        : growthCalculator.simulateAnnualGrowth(),
            };
        });

        const totalGrossSocialSecurity =
            primaryMemberSocialSecurityBenefit +
            (partnerMemberSocialSecurityBenefit ?? 0);

        // Calculate tax-aware distributions to meet target net amount
        const {
            distributions,
            effectiveTaxRate,
            totalSocialSecurityTax,
            targetMet,
        } = new RetirementDistributionCalculator(
            retirementExpenses,
            allAssets,
            this.inputs.memberInputs,
            year,
            this.inputs.householdInputs.taxFilingStatus,
            this.inputs.householdInputs.residentialLocation,
            totalGrossSocialSecurity,
        ).calculate();

        const retirementDrawdown = distributions.reduce((sum, dist) => {
            return sum + dist.grossDistributionAmount;
        }, 0);

        // Check if we have insufficient funds to meet the target net amount
        const insufficientFunds = !targetMet;

        const endingAssets = this.calculateEndingAssets(
            allAssets,
            contributions,
            growth,
            distributions,
        );

        return {
            year,
            primaryMemberAge,
            partnerMemberAge,
            primaryMemberYearType,
            partnerMemberYearType,
            primaryMemberSocialSecurityBenefit,
            partnerMemberSocialSecurityBenefit,
            totalSocialSecurityTax,
            retirementExpenses,
            retirementHealthcareCosts,
            longTermCareCosts,
            totalRetirementExpenses,
            retirementDrawdown,
            effectiveTaxRate,
            startingAssets,
            contributions,
            growth,
            distributions,
            endingAssets,
            insufficientFunds,
        };
    }

    private getRetirementExpenses(
        memberYearTypes: YearType[],
        year: number,
    ): {
        retirementExpenses: number;
        retirementHealthcareCosts: number;
        longTermCareCosts: number;
        totalRetirementExpenses: number;
    } {
        // the percent of members who are in retirement
        const retirementPercentage =
            memberYearTypes.filter(
                (yearType) => yearType === YearType.DECUMULATION,
            ).length / memberYearTypes.length;

        // Base annual spending in retirement (applied with retirement percentage)
        const baseExpenses =
            this.inputs.householdInputs.annualSpendingInRetirement *
            retirementPercentage;

        let retirementHealthcareCosts = 0;
        let longTermCareCosts = 0;

        this.inputs.memberInputs.forEach((member, memberIndex) => {
            if (member.includeRetirementHealthcareCosts) {
                // Check if this specific member is in decumulation (retirement)
                const memberYearType =
                    memberIndex === 0
                        ? memberYearTypes[0]
                        : (memberYearTypes[memberIndex] ?? memberYearTypes[0]);

                if (memberYearType === YearType.DECUMULATION) {
                    // Always add retirement healthcare cost for members in retirement
                    retirementHealthcareCosts +=
                        member.retirementHealthcareCost;

                    // Only add long-term care cost in the last 2 years of member's life
                    const memberAge = member.currentAge + year;
                    const yearsUntilEndOfLife =
                        member.lifeExpectancy - memberAge;

                    if (yearsUntilEndOfLife <= 2) {
                        longTermCareCosts += member.longTermCareCost;
                    }
                }
            }
        });

        const totalRetirementExpenses =
            baseExpenses + retirementHealthcareCosts + longTermCareCosts;

        return {
            retirementExpenses: roundToTwoDecimals(baseExpenses),
            retirementHealthcareCosts: roundToTwoDecimals(
                retirementHealthcareCosts,
            ),
            longTermCareCosts: roundToTwoDecimals(longTermCareCosts),
            totalRetirementExpenses: roundToTwoDecimals(
                totalRetirementExpenses,
            ),
        };
    }

    private calculateEndingAssets(
        allAssets: RetirementAssets,
        contributions: RetirementContributions,
        growth: RetirementGrowth,
        distributions: RetirementDistribution[],
    ): RetirementAssets {
        const endingAssets = cloneDeep(allAssets);

        // add any new accounts from contributions
        contributions.forEach(({ account, totalContribution }) => {
            const existingAccount = endingAssets.find(
                ({ id }) => id === account.id,
            );

            if (!existingAccount) {
                throw new Error(
                    `Account ${account.id} not found in existing accounts`,
                );
            } else {
                existingAccount.balance += totalContribution;

                // Update cost basis when contributions are made
                // For all account types, contributions increase the cost basis
                existingAccount.costBasis += totalContribution;
            }
        });

        // add growth to existing accounts
        growth.forEach(({ account, growth }) => {
            const existingAccount = endingAssets.find(
                ({ id }) => id === account.id,
            );

            if (!existingAccount) {
                throw new Error(
                    `Account ${account.id} not found in existing accounts`,
                );
            } else {
                existingAccount.balance += growth;
            }
        });

        // subtract distributions from existing accounts
        distributions.forEach(({ account, grossDistributionAmount }) => {
            const existingAccount = endingAssets.find(
                ({ id }) => id === account.id,
            );

            if (!existingAccount) {
                throw new Error(
                    `Account ${account.id} not found in existing accounts`,
                );
            } else {
                // Calculate proportional cost basis reduction before updating balance
                if (existingAccount.balance > 0) {
                    const withdrawalPercentage =
                        grossDistributionAmount / existingAccount.balance;
                    const costBasisReduction =
                        existingAccount.costBasis * withdrawalPercentage;
                    existingAccount.costBasis = roundToTwoDecimals(
                        Math.max(
                            0,
                            existingAccount.costBasis - costBasisReduction,
                        ),
                    );
                }

                existingAccount.balance -= grossDistributionAmount;
            }
        });

        // Round asset balances and cost basis.
        return endingAssets.map((asset) => {
            return {
                ...asset,
                balance: roundToTwoDecimals(asset.balance),
                costBasis: roundToTwoDecimals(asset.costBasis),
            };
        });
    }
}
